/**
 * Admin Console audit history (DEV-5969, RBAC plan §3.9 "view scoped audit
 * history" / §5.1).
 *
 * Read-only view over `GET /api/v1/admin/audit-events/`: the caller's scoped
 * trail, newest first, page-number paginated, filterable by target user,
 * action and time range. There is deliberately no control that writes, edits
 * or deletes an event — the table is append-only and the endpoint is `GET`
 * only.
 *
 * Tenancy is the server's: whatever cloud-core returns is what the caller may
 * see, and the view never filters rows client-side by company. The
 * `before`/`after` snapshots are rendered with their keys exactly as stored.
 */
import React, { FC, FormEvent, memo, useState } from "react";

import {
  AUDIT_ACTIONS,
  AUDIT_MAX_PAGE_SIZE,
  AuditEvent,
  AuditEventFilters,
  AuditSnapshot,
} from "Utils/adminAuditAPI";

import { useAdminAuditEvents } from "./useAdminAuditEvents";
import {
  ActionButtonStyled,
  AdminConsoleHeaderStyled,
  AdminConsoleTitleStyled,
  AdminTableStyled,
  AuditFilterFormStyled,
  AuditFilterErrorStyled,
  AuditPaginationStyled,
  AuditSnapshotStyled,
  EmptyStateStyled,
  InviteButtonStyled,
  InviteFieldStyled,
  SelectStyled,
  UserCountStyled,
} from "./styles";

export const PAGE_SIZE_OPTIONS = [25, 50, 100, AUDIT_MAX_PAGE_SIZE] as const;

/** `datetime-local` value (browser-local wall clock) → ISO-8601 for the API. */
export const toIsoTimestamp = (localValue: string): string | undefined => {
  const trimmed = localValue.trim();
  if (!trimmed) return undefined;
  const parsed = new Date(trimmed);
  // An unparsable value is sent as typed so the server's 400 names it, rather
  // than being silently dropped and answered with the unfiltered history.
  return Number.isNaN(parsed.getTime()) ? trimmed : parsed.toISOString();
};

const formatTimestamp = (iso: string): string => {
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? iso : parsed.toLocaleString();
};

const actorOf = (event: AuditEvent): string =>
  event.actorEmail || event.actorServiceId || event.actorUserId || "—";

const targetOf = (event: AuditEvent): string =>
  event.targetType || event.targetId
    ? [event.targetType, event.targetId].filter(Boolean).join(" ")
    : "—";

const Snapshot: FC<{ snapshot: AuditSnapshot; testId: string }> = ({
  snapshot,
  testId,
}) => {
  if (!snapshot || Object.keys(snapshot).length === 0) {
    return <span data-test-id={testId}>—</span>;
  }
  return (
    <AuditSnapshotStyled data-test-id={testId}>
      {Object.entries(snapshot).map(([key, value]) => (
        <div key={key}>
          <dt>{key}</dt>
          <dd>{typeof value === "string" ? value : JSON.stringify(value)}</dd>
        </div>
      ))}
    </AuditSnapshotStyled>
  );
};

interface FilterFormValues {
  targetUser: string;
  action: string;
  occurredAfter: string;
  occurredBefore: string;
}

const EMPTY_FORM: FilterFormValues = {
  targetUser: "",
  action: "",
  occurredAfter: "",
  occurredBefore: "",
};

const toFilters = (values: FilterFormValues): AuditEventFilters => ({
  targetUser: values.targetUser.trim() || undefined,
  action: values.action || undefined,
  occurredAfter: toIsoTimestamp(values.occurredAfter),
  occurredBefore: toIsoTimestamp(values.occurredBefore),
});

export const AuditHistory: FC = memo(() => {
  const {
    events,
    count,
    page,
    pageSize,
    hasNext,
    hasPrevious,
    isLoading,
    isLoadingError,
    filterError,
    reload,
    applyFilters,
    setPage,
    setPageSize,
  } = useAdminAuditEvents();

  const [form, setForm] = useState<FilterFormValues>(EMPTY_FORM);

  const updateForm =
    (field: keyof FilterFormValues) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setForm((current) => ({ ...current, [field]: event.target.value }));

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    applyFilters(toFilters(form));
  };

  const onClear = () => {
    setForm(EMPTY_FORM);
    applyFilters({});
  };

  return (
    <>
      <AdminConsoleHeaderStyled>
        <AdminConsoleTitleStyled>
          Audit History
          <UserCountStyled data-test-id="audit_count">{count}</UserCountStyled>
        </AdminConsoleTitleStyled>
      </AdminConsoleHeaderStyled>

      <AuditFilterFormStyled data-test-id="audit_filters" onSubmit={onSubmit}>
        <InviteFieldStyled>
          Target user ID
          <input
            data-test-id="audit_filter_target_user"
            placeholder="User UUID"
            value={form.targetUser}
            onChange={updateForm("targetUser")}
          />
        </InviteFieldStyled>
        <InviteFieldStyled>
          Action
          <SelectStyled
            data-test-id="audit_filter_action"
            value={form.action}
            onChange={updateForm("action")}
          >
            <option value="">Any action</option>
            {AUDIT_ACTIONS.map((action) => (
              <option key={action} value={action}>
                {action}
              </option>
            ))}
          </SelectStyled>
        </InviteFieldStyled>
        <InviteFieldStyled>
          From
          <input
            data-test-id="audit_filter_after"
            type="datetime-local"
            value={form.occurredAfter}
            onChange={updateForm("occurredAfter")}
          />
        </InviteFieldStyled>
        <InviteFieldStyled>
          To
          <input
            data-test-id="audit_filter_before"
            type="datetime-local"
            value={form.occurredBefore}
            onChange={updateForm("occurredBefore")}
          />
        </InviteFieldStyled>
        <InviteButtonStyled data-test-id="audit_filter_apply" type="submit">
          Apply
        </InviteButtonStyled>
        <ActionButtonStyled
          data-test-id="audit_filter_clear"
          type="button"
          onClick={onClear}
        >
          Clear
        </ActionButtonStyled>
        {filterError && (
          <AuditFilterErrorStyled
            role="alert"
            data-test-id="audit_filter_error"
          >
            {filterError}
          </AuditFilterErrorStyled>
        )}
      </AuditFilterFormStyled>

      {isLoading && (
        <EmptyStateStyled data-test-id="audit_loading">
          Loading history…
        </EmptyStateStyled>
      )}

      {!isLoading && isLoadingError && (
        <EmptyStateStyled data-test-id="audit_loading_error">
          Could not load audit history.{" "}
          <ActionButtonStyled data-test-id="audit_retry" onClick={reload}>
            Retry
          </ActionButtonStyled>
        </EmptyStateStyled>
      )}

      {!isLoading && !isLoadingError && events.length === 0 && (
        <EmptyStateStyled data-test-id="audit_empty">
          No audit events match.
        </EmptyStateStyled>
      )}

      {!isLoading && !isLoadingError && events.length > 0 && (
        <AdminTableStyled data-test-id="audit_events_table">
          <thead>
            <tr>
              <th>When</th>
              <th>Action</th>
              <th>Actor</th>
              <th>Target</th>
              <th>Company</th>
              <th>Before</th>
              <th>After</th>
              <th>Request ID</th>
            </tr>
          </thead>
          <tbody>
            {events.map((event) => (
              <tr key={event.id} data-test-id={`audit_event_${event.id}`}>
                <td>
                  <time dateTime={event.occurredAt}>
                    {formatTimestamp(event.occurredAt)}
                  </time>
                </td>
                <td data-test-id={`audit_action_${event.id}`}>
                  {event.action}
                </td>
                <td>{actorOf(event)}</td>
                <td>{targetOf(event)}</td>
                <td>{event.companyId ?? "—"}</td>
                <td>
                  <Snapshot
                    snapshot={event.before}
                    testId={`audit_before_${event.id}`}
                  />
                </td>
                <td>
                  <Snapshot
                    snapshot={event.after}
                    testId={`audit_after_${event.id}`}
                  />
                </td>
                <td>{event.requestId || "—"}</td>
              </tr>
            ))}
          </tbody>
        </AdminTableStyled>
      )}

      <AuditPaginationStyled data-test-id="audit_pagination">
        <ActionButtonStyled
          data-test-id="audit_page_previous"
          disabled={!hasPrevious || isLoading}
          onClick={() => setPage(page - 1)}
        >
          Previous
        </ActionButtonStyled>
        <span data-test-id="audit_page_label">Page {page}</span>
        <ActionButtonStyled
          data-test-id="audit_page_next"
          disabled={!hasNext || isLoading}
          onClick={() => setPage(page + 1)}
        >
          Next
        </ActionButtonStyled>
        <SelectStyled
          data-test-id="audit_page_size"
          value={pageSize}
          onChange={(event) => setPageSize(Number(event.target.value))}
        >
          {PAGE_SIZE_OPTIONS.map((size) => (
            <option key={size} value={size}>
              {size} per page
            </option>
          ))}
        </SelectStyled>
      </AuditPaginationStyled>
    </>
  );
});
